import * as React from "react";
import { UserContext, useUser } from "@/context/user";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getCookie } from "@/lib/utils";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

type Subscription = {
  subscribed: boolean;
  plan?: string;
};

type Payment = {
  id: string;
  amount: number;
  status: string;
  createdAt: string;
};

export default function ProfilePage() {
  const { user, fetchProfile } = useUser();
  const [isEditing, setIsEditing] = React.useState(false);
  const displayName = user?.data?.name || user?.data?.login;
  const [name, setName] = React.useState(displayName || "");
  const [subscription, setSubscription] = React.useState<Subscription | null>(
    null,
  );
  const [payments, setPayments] = React.useState<Payment[]>([]);

  React.useEffect(() => {
    if (user && user.data) {
      const newDisplayName = user.data.name || user.data.login || "";
      setName(newDisplayName);
    }
  }, [user]);

  React.useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const res = await fetch(
          import.meta.env.VITE_PUBLIC_API_ENDPOINT + "/payments/verify",
          {
            headers: {
              Authorization: getCookie("session") || "",
            },
            credentials: "include",
          },
        );
        if (res.ok) {
          const data = await res.json();
          setSubscription(data);
        }
      } catch (error) {
        console.error("Failed to fetch subscription", error);
      }
    };

    const fetchPayments = async () => {
      try {
        const res = await fetch(
          import.meta.env.VITE_PUBLIC_API_ENDPOINT + "/payments/history",
          {
            headers: {
              Authorization: getCookie("session") || "",
            },
            credentials: "include",
          },
        );
        if (res.ok) {
          const data = await res.json();
          setPayments(data.data);
        }
      } catch (error) {
        console.error("Failed to fetch payments", error);
      }
    };

    if (user) {
      fetchSubscription();
      fetchPayments();
    }
  }, [user]);

  if (!user) {
    return <div>Loading...</div>;
  }

  const handleSave = async () => {
    try {
      const res = await fetch(
        import.meta.env.VITE_PUBLIC_API_ENDPOINT + "/profile",
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: getCookie("session") || "",
          },
          credentials: "include",
          body: JSON.stringify({ name }),
        },
      );

      if (res.ok) {
        setIsEditing(false);
        fetchProfile(); // Refresh user data
      } else {
        console.error("Failed to update profile");
        // Optionally, show an error message to the user
      }
    } catch (error) {
      console.error("An error occurred while saving the profile", error);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setName(displayName || "");
  };

  return (
    <div className="container mx-auto p-4">
      <Card className="mx-auto w-full max-w-2xl">
        <CardHeader>
          <div className="flex items-center space-x-4">
            <Avatar className="border-sidebar-sidebar h-16 w-16 border-1">
              <AvatarImage src={user.data.avatar_url} alt={displayName} />
              <AvatarFallback>
                {displayName?.split(" ")[0]?.charAt(0)}
                {displayName?.split(" ")[1]?.charAt(0)}
              </AvatarFallback>
            </Avatar>
            <div>
              {isEditing ? (
                <Input
                  className="text-2xl font-bold"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              ) : (
                <CardTitle className="text-2xl">{displayName}</CardTitle>
              )}
              <CardDescription>{user.data.email}</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold">Subscription</h3>
            <div className="mt-2">
              <p>
                Subscription Status:{" "}
                {subscription?.subscribed ? "Active" : "Inactive"}
              </p>
              {subscription?.subscribed && <p>Plan: Scout</p>}
            </div>
          </div>
          <div>
            <h3 className="text-lg font-semibold">Payments</h3>
            <div className="mt-2">
              {payments.length > 0 ? (
                <ul>
                  {payments.map((payment) => (
                    <li key={payment.id}>
                      {payment.createdAt}: ${payment.amount / 100} -{" "}
                      {payment.status}
                    </li>
                  ))}
                </ul>
              ) : (
                <p>No payment history found.</p>
              )}
            </div>
          </div>
        </CardContent>
        <CardFooter className="flex justify-end space-x-2">
          {isEditing ? (
            <>
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button onClick={handleSave}>Save</Button>
            </>
          ) : (
            <Button onClick={() => setIsEditing(true)}>Edit Profile</Button>
          )}
        </CardFooter>
      </Card>
    </div>
  );
}
